import { memo } from 'react';
import { EarlyWarning } from '@/types/early-warning';

interface EarlyWarningTableProps {
  filteredData: EarlyWarning[];
  sortField: string;
  sortDirection: 'asc' | 'desc';
  handleSort: (field: string) => void;
  handleEditWarning: (warning: EarlyWarning) => void;
}

interface TableHeaderProps {
  field: string;
  label: string;
  sortField: string;
  sortDirection: 'asc' | 'desc';
  handleSort: (field: string) => void;
  className?: string;
}

const TableHeader = memo(
  ({
    field,
    label,
    sortField,
    sortDirection,
    handleSort,
    className = '',
  }: TableHeaderProps) => (
    <th
      scope="col"
      className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition ${className}`}
      onClick={() => handleSort(field)}
    >
      {label} {sortField === field && (sortDirection === 'asc' ? '↑' : '↓')}
    </th>
  )
);

TableHeader.displayName = 'TableHeader';

export const EarlyWarningTable = memo(
  ({
    filteredData,
    sortField,
    sortDirection,
    handleSort,
    handleEditWarning,
  }: EarlyWarningTableProps) => {
    const headerProps = { sortField, sortDirection, handleSort };

    return (
      <div className="rounded-lg border border-gray-200 shadow-sm overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <TableHeader field="id" label="System ID" {...headerProps} />
              <TableHeader field="reference" label="Reference" {...headerProps} />
              <TableHeader field="role" label="From" {...headerProps} />
              <TableHeader field="title" label="Title" {...headerProps} />
              <TableHeader field="replyDue" label="Reply Due" {...headerProps} />
              <TableHeader
                field="increasePrices"
                label="Price"
                className="text-center"
                {...headerProps}
              />
              <TableHeader
                field="delayCompletion"
                label="Completion"
                className="text-center"
                {...headerProps}
              />
              <TableHeader
                field="delayKeyDate"
                label="Key Date"
                className="text-center"
                {...headerProps}
              />
              <TableHeader
                field="impairPerformance"
                label="Performance"
                className="text-center"
                {...headerProps}
              />
              <TableHeader
                field="increaseContractorCost"
                label="Cost"
                className="text-center"
                {...headerProps}
              />
              <TableHeader field="score" label="Score" {...headerProps} />
              <TableHeader field="status" label="Status" {...headerProps} />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredData.length > 0 ? (
              filteredData.map((warning) => (
                <tr
                  key={warning.id}
                  className={
                    warning.status === 'OVERDUE'
                      ? 'bg-red-50 hover:bg-red-100'
                      : 'hover:bg-gray-50'
                  }
                  onClick={() => handleEditWarning(warning)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="px-4 py-3 text-sm font-medium">{warning.id}</td>
                  <td className="px-4 py-3 text-sm">
                    {warning.reference || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm">{warning.role}</td>
                  <td className="px-4 py-3 text-sm">{warning.title}</td>
                  <td className="px-4 py-3 text-sm">{warning.replyDue}</td>
                  <td className="px-4 py-3 text-sm text-center">
                    {warning.impacts?.increasePrices ? '✓' : ''}
                  </td>
                  <td className="px-4 py-3 text-sm text-center">
                    {warning.impacts?.delayCompletion ? '✓' : ''}
                  </td>
                  <td className="px-4 py-3 text-sm text-center">
                    {warning.impacts?.delayKeyDate ? '✓' : ''}
                  </td>
                  <td className="px-4 py-3 text-sm text-center">
                    {warning.impacts?.impairPerformance ? '✓' : ''}
                  </td>
                  <td className="px-4 py-3 text-sm text-center">
                    {warning.impacts?.increaseContractorCost ? '✓' : ''}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        warning.score >= 4
                          ? 'bg-red-100 text-red-800'
                          : warning.score >= 3
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {warning.score}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        warning.status === 'OVERDUE'
                          ? 'bg-red-100 text-red-800'
                          : warning.status === 'ACTIVE'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {warning.status}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={12} className="px-4 py-6 text-center text-gray-500">
                  No early warnings found
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="flex justify-between items-center p-4 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            {filteredData.length > 0
              ? `Showing ${filteredData.length} early warning${filteredData.length !== 1 ? 's' : ''}`
              : 'No early warnings'}
          </div>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 text-sm rounded-md border border-gray-300 bg-white text-gray-400"
              disabled
            >
              Previous
            </button>
            <button
              className="px-3 py-1 text-sm rounded-md border border-gray-300 bg-white text-gray-400"
              disabled
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  }
);

EarlyWarningTable.displayName = 'EarlyWarningTable';
